/**
 * CombatDock — between-panel combat zone.
 *
 * Shows each unresolved CombatSlot as a row: attacker side (left) | block side (right),
 * each with the played card name, current power/block value, and ± nudge buttons.
 *
 * If a slot is in defender-pre-block with no block yet (block === null), the
 * defender sees a "Take Dmg" button. Once both sides have committed, the engine
 * auto-fires resolveCombat (parent does this on a useEffect).
 */
import type { Card } from "@/types/card";
import type { CombatSlot, GameState } from "@/types/game";
import { Button } from "@/components/ui/Button";
import { instanceValue, syntheticCardFor } from "@/game/playtestEngine";
import { Plus, Minus } from "lucide-react";

interface CombatDockProps {
  game: GameState;
  cardMap: Map<string, Card>;
  selfNum: 1 | 2;
  /** Adjust power/block on a played-instance copy. */
  onAdjustValue: (
    n: 1 | 2,
    instanceId: string,
    cardDbId: string,
    mode: "attack" | "defend",
    delta: number,
  ) => void;
  /** Defender clicks "Take Dmg" when an unblocked attack is on them. */
  onTakeDamage: (defenderNum: 1 | 2) => void;
  /** Active player passes priority. Always visible inside the combat dock. */
  onPass?: () => void;
  /** Whether passing is currently allowed (active priority + not in mid-resolution). */
  canPass?: boolean;
}

export function CombatDock({
  game,
  cardMap,
  selfNum,
  onAdjustValue,
  onTakeDamage,
  onPass,
  canPass = true,
}: CombatDockProps) {
  const slots = game.combatSlots ?? [];

  const passButton = onPass ? (
    <button
      type="button"
      onClick={onPass}
      disabled={!canPass}
      className="rounded-md border border-amber-soft/60 bg-amber-500/20 px-4 py-1.5 text-[12px] font-bold uppercase tracking-widest text-amber-soft shadow-sm hover:bg-amber-500/30 disabled:cursor-not-allowed disabled:opacity-40"
      title="Pass priority"
    >
      Pass
    </button>
  ) : null;

  if (slots.length === 0) {
    return (
      <div className="relative flex h-full w-full items-center justify-center rounded-md border border-dashed border-[#2a2f3a] bg-[#0e1014] text-[11px] uppercase tracking-widest text-[#64748b]">
        <span>⚔ Combat Zone — no exchange in progress</span>
        {passButton && (
          <div className="absolute inset-0 flex items-center justify-center">
            {passButton}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative flex h-full w-full flex-col rounded-md border border-[#2a2f3a] bg-[#0e1014] p-2">
      <div className="mb-1 flex items-center justify-between text-[11px] uppercase tracking-widest text-[#64748b]">
        <span>⚔ Combat Zone</span>
        {game.subPhase && (
          <span className="font-mono text-[#94a3b8]">{game.subPhase}</span>
        )}
      </div>

      {/* Vertical scroll, hidden scrollbar: each slot is a full-width row.
          Newest attack is rendered FIRST so it's always visible at the top
          without scrolling; older exchanges sit below and scroll into view. */}
      <div
        className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overflow-x-hidden scrollbar-none"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {[...slots].reverse().map((slot) => (
          <div key={slot.id} className="w-full flex-shrink-0">
            <SlotRow
              slot={slot}
              game={game}
              cardMap={cardMap}
              selfNum={selfNum}
              onAdjustValue={onAdjustValue}
              onTakeDamage={onTakeDamage}
            />
          </div>
        ))}
      </div>

      {/* Centered Pass button overlay — visible during active combat. */}
      {passButton && (
        <div className="pointer-events-none absolute inset-x-0 bottom-1 flex justify-center">
          <div className="pointer-events-auto">{passButton}</div>
        </div>
      )}
    </div>
  );
}

function SlotRow({
  slot,
  game,
  cardMap,
  selfNum,
  onAdjustValue,
  onTakeDamage,
}: {
  slot: CombatSlot;
  game: GameState;
  cardMap: Map<string, Card>;
  selfNum: 1 | 2;
  onAdjustValue: CombatDockProps["onAdjustValue"];
  onTakeDamage: CombatDockProps["onTakeDamage"];
}) {
  const attackerNum = slot.attackerNum;
  const defenderNum: 1 | 2 = attackerNum === 1 ? 2 : 1;
  const attackerPlayer = game.players[attackerNum];
  const defenderPlayer = game.players[defenderNum];

  // Synthetic (Reflect) attacks don't have a real card in cardMap; render
  // them via syntheticCardFor so the dock row still shows name + power.
  const attackCard = slot.synthetic
    ? syntheticCardFor(slot) ?? undefined
    : cardMap.get(slot.attackCard.cardId);
  const blockCard = slot.blockCard ? cardMap.get(slot.blockCard.cardId) : null;

  const attackPower = attackCard
    ? instanceValue(attackerPlayer, slot.attackCard.id, "attack", attackCard)
    : 0;
  const blockValue =
    blockCard && slot.blockCard
      ? instanceValue(defenderPlayer, slot.blockCard.id, "defend", blockCard)
      : 0;

  const isMyAttack = attackerNum === selfNum;
  const isMyBlock = defenderNum === selfNum;
  const canTakeDmg =
    !slot.blockCard &&
    !slot.resolved &&
    selfNum === defenderNum &&
    game.subPhase === "defender-pre-block" &&
    game.priority === defenderNum;

  // Outcome label (only for resolved slots).
  let outcome: { label: string; tone: string } | null = null;
  if (slot.resolved) {
    if (slot.damageTaken === 0) {
      outcome = { label: "Fully Blocked", tone: "bg-blue-500/30 text-blue-200" };
    } else if (!slot.blockCard) {
      outcome = { label: `${slot.damageTaken} dmg (unblocked)`, tone: "bg-red-500/30 text-red-200" };
    } else {
      outcome = { label: `${slot.damageTaken} dmg dealt`, tone: "bg-red-500/30 text-red-200" };
    }
  }

  return (
    <div className="grid h-full grid-cols-[1fr_auto_1fr] items-stretch gap-2 rounded border border-[#2a2f3a] bg-[#13151a] p-2 text-xs">
      {/* Attack side */}
      <CardMini
        playerLabel={`⚔ ${attackerPlayer.name}`}
        playerTone="bg-red-500/20 text-red-300"
        card={attackCard ?? null}
        value={attackPower}
        valueLabel="PWR"
        valueTone="bg-red-500/30 text-red-200"
        accent="border-red-500/60"
        canAdjust={isMyAttack && !slot.resolved}
        onAdjust={(d) =>
          attackCard &&
          onAdjustValue(attackerNum, slot.attackCard.id, attackCard.id, "attack", d)
        }
        granted={slot.attackGrantedKeywords ?? []}
        side="attack"
      />

      {/* Divider with outcome chip */}
      <div className="flex flex-col items-center justify-center gap-1 text-[#64748b]">
        <span className="text-[10px] font-semibold uppercase tracking-widest">vs</span>
        {outcome && (
          <span
            className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${outcome.tone}`}
          >
            {outcome.label}
          </span>
        )}
      </div>

      {/* Block side */}
      {blockCard && slot.blockCard ? (
        <CardMini
          playerLabel={`🛡 ${defenderPlayer.name}`}
          playerTone="bg-blue-500/20 text-blue-300"
          card={blockCard}
          value={blockValue}
          valueLabel="BLK"
          valueTone="bg-blue-500/30 text-blue-200"
          accent="border-blue-500/60"
          canAdjust={isMyBlock && !slot.resolved}
          onAdjust={(d) =>
            blockCard &&
            slot.blockCard &&
            onAdjustValue(defenderNum, slot.blockCard!.id, blockCard.id, "defend", d)
          }
          granted={slot.blockGrantedKeywords ?? []}
          side="defend"
        />
      ) : canTakeDmg ? (
        <div className="flex items-center justify-center">
          <Button size="sm" variant="secondary" onClick={() => onTakeDamage(defenderNum)}>
            Take {attackPower} Dmg
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-center text-[10px] uppercase tracking-wider text-[#64748b]">
          {slot.resolved ? "—" : "awaiting response…"}
        </div>
      )}
    </div>
  );
}

// ─── Attack/Defend zone mini card with full visual + keywords ───────────────────

function CardMini({
  playerLabel,
  playerTone,
  card,
  value,
  valueLabel,
  valueTone,
  accent,
  canAdjust,
  onAdjust,
  granted,
  side,
}: {
  playerLabel: string;
  playerTone: string;
  card: Card | null;
  value: number;
  valueLabel: string;
  valueTone: string;
  accent: string;
  canAdjust: boolean;
  onAdjust: (delta: number) => void;
  granted: string[];
  side: "attack" | "defend";
}) {
  const baseKw = card?.keywords ?? [];
  const sideKw = side === "attack" ? (card?.attackKeywords ?? []) : (card?.defendKeywords ?? []);
  const allKw = Array.from(new Set([...baseKw, ...sideKw, ...granted]));
  const weight = card?.attackDefendFields?.weight ?? card?.signatureMoveFields?.attackWeight ?? "";
  const attackType =
    card?.attackDefendFields?.attackType ??
    card?.attackDefendFields?.defendType ??
    card?.signatureMoveFields?.attackType ??
    "";

  return (
    <div className="flex items-center gap-2">
      {/* Mini card visual */}
      <div
        className={`relative h-20 w-14 shrink-0 overflow-hidden rounded border-2 bg-[#13151a] ${accent}`}
      >
        {card?.imageDataUrl ? (
          <img
            src={card.imageDataUrl}
            alt={card.name}
            className="absolute inset-0 h-full w-full object-cover"
            draggable={false}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <span className="text-2xl font-black text-white/10">
              {card?.cardType?.[0] ?? "?"}
            </span>
          </div>
        )}
        {/* Big value overlay */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span
            className={`text-3xl font-black drop-shadow-[0_0_6px_rgba(0,0,0,0.95)] ${
              side === "attack" ? "text-red-300" : "text-blue-300"
            }`}
          >
            {value}
          </span>
        </div>
      </div>

      {/* Right column: name, badges, keywords, adjust */}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-1">
          <span className={`rounded px-1.5 py-px text-[9px] uppercase tracking-wider ${playerTone}`}>
            {playerLabel}
          </span>
        </div>
        <span className="truncate text-[11px] font-semibold leading-tight text-white">
          {card?.name ?? "?"}
        </span>
        <div className="flex items-center gap-1">
          {canAdjust && (
            <button
              type="button"
              onClick={() => onAdjust(-1)}
              className="rounded border border-[#2a2f3a] p-0.5 text-[#94a3b8] hover:bg-[#22262f]"
            >
              <Minus size={9} />
            </button>
          )}
          <span className={`rounded px-1.5 py-px font-mono text-[10px] ${valueTone}`}>
            {valueLabel} {value}
          </span>
          {canAdjust && (
            <button
              type="button"
              onClick={() => onAdjust(1)}
              className="rounded border border-[#2a2f3a] p-0.5 text-[#94a3b8] hover:bg-[#22262f]"
            >
              <Plus size={9} />
            </button>
          )}
        </div>
        {(weight || attackType) && (
          <div className="flex flex-wrap gap-0.5">
            {weight && (
              <span className="rounded bg-amber-500/20 px-1 py-px text-[8px] uppercase tracking-wide text-amber-300">
                {weight}
              </span>
            )}
            {attackType && (
              <span className="rounded bg-blue-500/20 px-1 py-px text-[8px] uppercase tracking-wide text-blue-300">
                {attackType}
              </span>
            )}
          </div>
        )}
        {allKw.length > 0 && (
          <div className="flex flex-wrap gap-0.5">
            {allKw.slice(0, 3).map((k) => (
              <span
                key={k}
                className="rounded bg-violet-500/20 px-1 py-px text-[8px] uppercase tracking-wide text-violet-300"
              >
                {k}
              </span>
            ))}
            {allKw.length > 3 && (
              <span className="text-[8px] text-[#64748b]">+{allKw.length - 3}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
